/**
 * bpmn-layout-improvements.ts — V5 FINALE
 *
 * ── Stratégie ────────────────────────────────────────────────────
 *  1. saveXML()              → XML courant (IDs stables)
 *  2. bpmnLayoutProcess(xml) → XML layouté (positions + routes calculées)
 *  3. extractShapeBounds()   → DOMParser lit les bounds DI du XML layouté
 *  4. moveShape()            → repositionne les shapes SANS importXML
 *  5. layouter.layoutConnection() → recalcule les waypoints
 *  6. removeScriptIcons()    → supprime les icônes script via DOM
 *  7. fitDiagramToViewport()
 *
 *  Pas d'importXML → IDs stables → propriétés préservées ✅
 *  Connexions recalculées par bpmn-js → flèches correctes ✅
 *  Icônes script supprimées via DOM → nœuds propres ✅
 */

// @ts-ignore
import { layoutProcess as bpmnLayoutProcess } from "bpmn-auto-layout";

// ── Types ─────────────────────────────────────────────────────────

interface Position {
  x: number;
  y: number;
  width?: number;
  height?: number;
}

// ── Constantes ────────────────────────────────────────────────────

const DEFAULT_NODE_W = 120;
const DEFAULT_NODE_H = 60;
const GAP_HORIZONTAL = 280;
const GAP_VERTICAL = 360;
const GAP_GRID_H = 220;
const GAP_GRID_V = 160;
const GAP_AFTER_GRID = 280;
const GRID_THRESHOLD = 5;
const ORIGIN_X = 500;
const ORIGIN_Y = 150;
const VIEWPORT_PADDING = 140;
const OVERLAP_PADDING = 40;
const OVERLAP_MAX_ITER = 50;
const OVERLAP_STOP_FORCE = 0.3;
const OVERLAP_DAMPING = 0.7;
const MOVE_THRESHOLD = 0.5;

// ── Suppression icônes script via DOM ─────────────────────────────

/**
 * Parcourt tous les shapes bpmn:ScriptTask dans le canvas et
 * supprime le <path> de l'icône script (dernier path direct de .djs-visual
 * sans attribut data-marker).
 *
 * Appelé après moveShape + layoutConnection, quand le DOM est stable.
 * Fonctionne indépendamment du renderer bpmn-js.
 */
function removeScriptIcons(elementRegistry: any): void {
  try {
    const scriptTasks = (elementRegistry.getAll() as any[]).filter(
      (el: any) => el.type === "bpmn:ScriptTask",
    );

    let removed = 0;
    scriptTasks.forEach((shape: any) => {
      const gfx: SVGElement | undefined = elementRegistry.getGraphics(shape);
      if (!gfx) return;

      const visual = gfx.querySelector(".djs-visual");
      if (!visual) return;

      // Supprimer TOUS les enfants de .djs-visual sauf le premier (rect)
      // et sauf les <g> (labels). L'icône script est un <path> supplémentaire.
      // On utilise display:none plutôt que removeChild pour éviter les
      // erreurs si bpmn-js tente de redessiner
      const children = Array.from(visual.children) as Element[];

      // Stratégie : cacher tout path qui n'est pas le rect principal
      // Le rect est un <rect>, les labels sont dans des <g>
      // L'icône script est un <path> sans data-marker
      children.forEach((child) => {
        const tag = child.tagName.toLowerCase();
        if (tag === "path" && !child.hasAttribute("data-marker")) {
          (
            child as unknown as SVGElement & { style: CSSStyleDeclaration }
          ).style.display = "none";
          removed++;
        }
      });

      // Fallback : si rien trouvé avec path, chercher dans tous les descendants
      if (removed === 0) {
        const allPaths = visual.querySelectorAll("path");
        // Le premier path est généralement le rect converti — ignorer
        // On cherche les paths avec une forme complexe (l'icône script a beaucoup de segments)
        allPaths.forEach((p) => {
          const d = p.getAttribute("d") || "";
          // L'icône script a une forme complexe avec plusieurs segments M/L/C
          if (
            !p.hasAttribute("data-marker") &&
            (d.match(/[ML]/g) || []).length > 4
          ) {
            (
              p as unknown as SVGElement & { style: CSSStyleDeclaration }
            ).style.display = "none";
            removed++;
          }
        });
      }
    });

    console.log(`[bpmn-layout] ${removed} icône(s) script supprimée(s)`);
  } catch (e) {
    console.warn("[bpmn-layout] removeScriptIcons error:", e);
  }
}

// ── Extraction bounds depuis XML layouté ─────────────────────────

interface ShapeBound {
  x: number;
  y: number;
  width: number;
  height: number;
}

function extractShapeBounds(xml: string): Record<string, ShapeBound> {
  const bounds: Record<string, ShapeBound> = {};
  try {
    const doc = new DOMParser().parseFromString(xml, "application/xml");
    doc
      .querySelectorAll("BPMNShape, [*|localName='BPMNShape']")
      .forEach((el) => {
        const bpmnElement =
          el.getAttribute("bpmnElement") ||
          el.getAttributeNS(null, "bpmnElement");
        if (!bpmnElement) return;
        const b =
          el.querySelector("Bounds") ||
          el.querySelector("[*|localName='Bounds']");
        if (!b) return;
        const x = parseFloat(b.getAttribute("x") ?? "0");
        const y = parseFloat(b.getAttribute("y") ?? "0");
        const width = parseFloat(
          b.getAttribute("width") ?? String(DEFAULT_NODE_W),
        );
        const height = parseFloat(
          b.getAttribute("height") ?? String(DEFAULT_NODE_H),
        );
        if (!isNaN(x) && !isNaN(y))
          bounds[bpmnElement] = { x, y, width, height };
      });
  } catch (e) {
    console.warn("[bpmn-layout] extractShapeBounds error:", e);
  }
  return bounds;
}

// ── Application positions + recalcul connexions ───────────────────

function applyShapesAndReconnect(
  shapeBounds: Record<string, ShapeBound>,
  elementRegistry: any,
  modeling: any,
  layouter: any,
): void {
  const allElements = elementRegistry.getAll() as any[];
  let shapesApplied = 0;

  Object.entries(shapeBounds).forEach(([bpmnId, b]) => {
    let shape: any = elementRegistry.get(bpmnId);
    if (!shape) {
      shape = allElements.find(
        (el: any) => el.businessObject?.id === bpmnId || el.id === bpmnId,
      );
    }
    if (!shape || shape.x === undefined || shape.waypoints) return;

    const dx = b.x - shape.x;
    const dy = b.y - shape.y;
    if (Math.abs(dx) > MOVE_THRESHOLD || Math.abs(dy) > MOVE_THRESHOLD) {
      try {
        modeling.moveShape(shape, { x: dx, y: dy });
        shapesApplied++;
      } catch {
        /* silencieux */
      }
    }
  });

  console.log(`[bpmn-layout] ${shapesApplied} shapes repositionnés`);

  // Recalcul des connexions après un tick
  setTimeout(() => {
    const connections = (elementRegistry.getAll() as any[]).filter(
      (el: any) =>
        el.type === "bpmn:SequenceFlow" ||
        el.type === "bpmn:MessageFlow" ||
        el.type === "bpmn:Association",
    );
    let ok = 0;
    connections.forEach((conn: any) => {
      try {
        if (layouter?.layoutConnection) {
          layouter.layoutConnection(conn);
          ok++;
        }
      } catch {
        /* silencieux */
      }
    });
    console.log(`[bpmn-layout] ${ok} connexions recalculées`);
  }, 50);
}

// ── bpmn-auto-layout ──────────────────────────────────────────────

async function tryBpmnAutoLayout(xml: string): Promise<string | null> {
  try {
    const result = await bpmnLayoutProcess(xml);
    if (typeof result === "string" && result.length > 0) return result;
    if (result?.xml) return result.xml;
    return null;
  } catch (e) {
    console.warn("[bpmn-layout] bpmn-auto-layout error:", e);
    return null;
  }
}

// ── BFS fallback ──────────────────────────────────────────────────

function buildHierarchy(
  data: any,
  startNodeCode: string,
): { groups: Record<number, string[]> } {
  const nodes = data.nodes || {};
  const links = data.links || [];
  const adj: Record<string, string[]> = {};
  Object.keys(nodes).forEach((k) => (adj[k] = []));
  links.forEach((l: any) => {
    const s = l.source?.node,
      t = l.target?.node;
    if (s && t && adj[s]) adj[s].push(t);
  });
  const level: Record<string, number> = {};
  const queue = [startNodeCode];
  level[startNodeCode] = 0;
  while (queue.length) {
    const cur = queue.shift()!;
    (adj[cur] || []).forEach((nxt) => {
      if (level[nxt] === undefined) {
        level[nxt] = level[cur] + 1;
        queue.push(nxt);
      }
    });
  }
  const maxLvl = Math.max(0, ...Object.values(level));
  Object.keys(nodes).forEach((k) => {
    if (level[k] === undefined) level[k] = maxLvl + 1;
  });
  const groups: Record<number, string[]> = {};
  Object.entries(level).forEach(([k, lv]) => {
    if (!groups[lv]) groups[lv] = [];
    groups[lv].push(k);
  });
  return { groups };
}

function calcGridDims(count: number): { cols: number; rows: number } {
  let bestCols = 1,
    bestDiff = count - 1;
  for (let c = 1; c <= Math.ceil(Math.sqrt(count)); c++) {
    const diff = Math.abs(c - Math.ceil(count / c));
    if (diff < bestDiff) {
      bestDiff = diff;
      bestCols = c;
    }
  }
  return { cols: bestCols, rows: Math.ceil(count / bestCols) };
}

// ── Exports publics ───────────────────────────────────────────────

export function computeHierarchicalLayout(
  data: any,
  startNodeCode: string,
  _elementRegistry: any,
): Record<string, Position> {
  const { groups } = buildHierarchy(data, startNodeCode);
  const positions: Record<string, Position> = {};
  let currentY = ORIGIN_Y;
  Object.entries(groups)
    .sort(([a], [b]) => Number(a) - Number(b))
    .forEach(([, nodesAtLevel]) => {
      const count = nodesAtLevel.length;
      if (count >= GRID_THRESHOLD) {
        const { cols, rows } = calcGridDims(count);
        const gW = cols * DEFAULT_NODE_W + (cols - 1) * GAP_GRID_H;
        const gH = rows * DEFAULT_NODE_H + (rows - 1) * GAP_GRID_V;
        const sx = ORIGIN_X - gW / 2;
        nodesAtLevel.forEach((code, idx) => {
          const row = Math.floor(idx / cols),
            col = idx % cols;
          positions[code] = {
            x: sx + col * (DEFAULT_NODE_W + GAP_GRID_H) + DEFAULT_NODE_W / 2,
            y:
              currentY +
              row * (DEFAULT_NODE_H + GAP_GRID_V) +
              DEFAULT_NODE_H / 2,
            width: DEFAULT_NODE_W,
            height: DEFAULT_NODE_H,
          };
        });
        currentY += gH + GAP_AFTER_GRID;
      } else {
        const tw = count * DEFAULT_NODE_W + (count - 1) * GAP_HORIZONTAL;
        const sx = ORIGIN_X - tw / 2;
        nodesAtLevel.forEach((code, i) => {
          positions[code] = {
            x: sx + i * (DEFAULT_NODE_W + GAP_HORIZONTAL) + DEFAULT_NODE_W / 2,
            y: currentY,
            width: DEFAULT_NODE_W,
            height: DEFAULT_NODE_H,
          };
        });
        currentY += DEFAULT_NODE_H + GAP_VERTICAL;
      }
    });
  return positions;
}

export function resolveNodeOverlaps(
  positions: Record<string, Position>,
): Record<string, Position> {
  const entries = Object.entries(positions);
  if (entries.length < 2) return positions;
  const vel: Record<string, { vx: number; vy: number }> = {};
  entries.forEach(([k]) => {
    vel[k] = { vx: 0, vy: 0 };
  });
  let iter = 0,
    maxForce = Infinity;
  while (iter < OVERLAP_MAX_ITER && maxForce > OVERLAP_STOP_FORCE) {
    iter++;
    maxForce = 0;
    entries.forEach(([k]) => {
      vel[k] = { vx: 0, vy: 0 };
    });
    for (let i = 0; i < entries.length; i++) {
      for (let j = i + 1; j < entries.length; j++) {
        const [cA, pA] = entries[i],
          [cB, pB] = entries[j];
        const dx = pB.x - pA.x,
          dy = pB.y - pA.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const minD = Math.sqrt(
          Math.pow(
            (pA.width ?? DEFAULT_NODE_W) / 2 +
              (pB.width ?? DEFAULT_NODE_W) / 2 +
              OVERLAP_PADDING,
            2,
          ) +
            Math.pow(
              (pA.height ?? DEFAULT_NODE_H) / 2 +
                (pB.height ?? DEFAULT_NODE_H) / 2 +
                OVERLAP_PADDING,
              2,
            ),
        );
        if (dist < minD) {
          const f = (minD - dist) / dist;
          const fx = (dx / dist) * f,
            fy = (dy / dist) * f;
          vel[cA].vx -= fx;
          vel[cA].vy -= fy;
          vel[cB].vx += fx;
          vel[cB].vy += fy;
          maxForce = Math.max(maxForce, Math.abs(fx), Math.abs(fy));
        }
      }
    }
    entries.forEach(([c, p]) => {
      p.x += vel[c].vx * OVERLAP_DAMPING;
      p.y += vel[c].vy * OVERLAP_DAMPING;
    });
  }
  return positions;
}

export function applyLayoutPositions(
  modeling: any,
  elementRegistry: any,
  positions: Record<string, Position>,
): void {
  const elements = elementRegistry.getAll();
  let applied = 0;
  Object.entries(positions).forEach(([code, pos]) => {
    const shape = elements.find(
      (el: any) => (el.businessObject?.code || el.id) === code,
    );
    if (shape && pos.x !== undefined && shape.x !== undefined) {
      try {
        const dx = pos.x - shape.x,
          dy = pos.y - shape.y;
        if (Math.abs(dx) > MOVE_THRESHOLD || Math.abs(dy) > MOVE_THRESHOLD) {
          modeling.moveShape(shape, { x: dx, y: dy });
          applied++;
        }
      } catch {
        /* silencieux */
      }
    }
  });
  console.log(
    `[bpmn-layout] ${applied} nœud(s) repositionné(s) (BFS fallback)`,
  );
}

export function optimizeConnectionRouting(
  _modeling: any,
  elementRegistry: any,
  layouter: any,
): void {
  if (!layouter?.layoutConnection) return;
  const conns = elementRegistry
    .getAll()
    .filter((el: any) => el.type === "bpmn:SequenceFlow");
  let ok = 0;
  conns.forEach((c: any) => {
    try {
      layouter.layoutConnection(c);
      ok++;
    } catch {
      /* silencieux */
    }
  });
  console.log(`[bpmn-layout] ${ok} connexion(s) re-routées (BFS fallback)`);
}

export function fitDiagramToViewport(canvas: any, elementRegistry: any): void {
  if (!canvas || !elementRegistry) return;
  const els = elementRegistry.getAll();
  if (!els.length) return;
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  els.forEach((el: any) => {
    if (el.x === undefined) return;
    const w = el.width || DEFAULT_NODE_W,
      h = el.height || DEFAULT_NODE_H;
    minX = Math.min(minX, el.x - w / 2);
    minY = Math.min(minY, el.y - h / 2);
    maxX = Math.max(maxX, el.x + w / 2);
    maxY = Math.max(maxY, el.y + h / 2);
  });
  if (!isFinite(minX)) return;
  try {
    canvas.viewbox({
      x: minX - VIEWPORT_PADDING,
      y: minY - VIEWPORT_PADDING,
      width: maxX - minX + 2 * VIEWPORT_PADDING,
      height: maxY - minY + 2 * VIEWPORT_PADDING,
    });
  } catch {
    /* silencieux */
  }
}

export async function computeLayoutWithELK(
  _er: any,
  _n: any,
  _l: any,
): Promise<Record<string, Position>> {
  return {};
}

// ── Fix z-order : shapes devant les connexions ────────────────────

/**
 * En SVG, l'ordre de peinture = ordre dans le DOM.
 * bpmn-js crée les connexions après les shapes donc elles s'affichent
 * par-dessus. On réordonne : tous les djs-shape après les djs-connection.
 *
 * Stratégie : dans chaque groupe parent, déplacer les <g class="djs-group">
 * contenant un .djs-shape à la fin (= au premier plan).
 */
function fixShapeZOrder(elementRegistry: any): void {
  try {
    // Récupérer tous les containers de shapes et connexions
    const canvas = document.getElementById("js-canvas");
    if (!canvas) return;

    // Trouver tous les groupes qui contiennent des connexions
    // et s'assurer que les shapes sont après dans le DOM
    const allShapeGroups = canvas.querySelectorAll(".djs-element.djs-shape");
    let moved = 0;

    allShapeGroups.forEach((shapeEl) => {
      // Le parent immédiat est .djs-group, son parent est le container SVG
      const shapeGroup = shapeEl.parentElement; // .djs-group
      if (!shapeGroup) return;
      const parentContainer = shapeGroup.parentElement;
      if (!parentContainer) return;

      // Vérifier si des connexions sont APRÈS ce shape dans le même parent
      const siblings = Array.from(parentContainer.children);
      const shapeIdx = siblings.indexOf(shapeGroup);
      const hasConnectionAfter = siblings
        .slice(shapeIdx + 1)
        .some((s) => s.querySelector(".djs-connection") !== null);

      // Si oui, déplacer le shape à la fin du parent
      if (hasConnectionAfter) {
        parentContainer.appendChild(shapeGroup);
        moved++;
      }
    });

    if (moved > 0) {
      console.log(`[bpmn-layout] ${moved} shape(s) remontés au premier plan`);
    }
  } catch (e) {
    console.warn("[bpmn-layout] fixShapeZOrder error:", e);
  }
}

// ── Point d'entrée unique ─────────────────────────────────────────

export async function applyFullLayout(
  elementRegistry: any,
  modeling: any,
  canvas: any,
  layouter: any,
  data: any,
  startCode: string,
  modeler?: any,
): Promise<boolean> {
  if (!startCode) {
    console.warn("[bpmn-layout] startCode manquant");
    return false;
  }

  const fitViewport = () => {
    setTimeout(() => {
      try {
        fitDiagramToViewport(canvas, elementRegistry);
      } catch {
        /* silencieux */
      }
    }, 300);
  };

  // ── Chemin principal : bpmn-auto-layout + extraction DI ──────────
  if (modeler) {
    try {
      const { xml } = await modeler.saveXML({ format: true });
      if (xml) {
        const layoutedXml = await tryBpmnAutoLayout(xml);
        if (layoutedXml) {
          const shapeBounds = extractShapeBounds(layoutedXml);
          const shapeCount = Object.keys(shapeBounds).length;

          if (shapeCount > 0) {
            applyShapesAndReconnect(
              shapeBounds,
              elementRegistry,
              modeling,
              layouter,
            );

            // Supprimer les icônes script après que les connexions ont été
            // recalculées (setTimeout 50ms dans applyShapesAndReconnect)
            // → on attend 150ms pour être sûr que le DOM est stable
            setTimeout(() => {
              removeScriptIcons(elementRegistry);
              fixShapeZOrder(elementRegistry);
            }, 150);

            fitViewport();
            console.log(
              `[bpmn-layout] ✅ V5 — ${shapeCount} shapes extraits du DI`,
            );
            return true;
          } else {
            console.warn(
              "[bpmn-layout] extractShapeBounds : 0 shapes, fallback BFS",
            );
          }
        }
      }
    } catch (e) {
      console.warn("[bpmn-layout] chemin principal échoué, fallback BFS :", e);
    }
  }

  // ── Fallback BFS ─────────────────────────────────────────────────
  try {
    let positions = computeHierarchicalLayout(data, startCode, elementRegistry);
    positions = resolveNodeOverlaps(positions);
    applyLayoutPositions(modeling, elementRegistry, positions);
    await new Promise<void>((r) => setTimeout(r, 100));
    optimizeConnectionRouting(modeling, elementRegistry, layouter);
    setTimeout(() => {
      removeScriptIcons(elementRegistry);
      fixShapeZOrder(elementRegistry);
    }, 200);
    fitViewport();
    console.log("[bpmn-layout] ⚠️ Fallback BFS appliqué");
    return true;
  } catch (err) {
    console.error("[bpmn-layout] ❌ applyFullLayout échoué :", err);
    return false;
  }
}
